/**
 * The intro's opening logo sequence, frame by frame: a line sweeps across,
 * beats like a heart monitor, curls into a heart that pulses, turns, and
 * becomes the CertainPath paperclip mark as the name writes in.
 *
 * Every shape is a start point plus 12 cubic curves, so each morphs point
 * by point into the next. Coordinates are in the logo SVG's viewBox
 * (0 0 2676.68 583.97); the mark is centred on (208, 292).
 */

export const LOGO_DURATION = 11.8;

const CX = 208;
const CY = 292;
const LINE_LENGTH = 2016;
const ECG_BEATS = [1.2, 1.95, 2.7, 3.45];
const HEART_BEATS = [5.25, 5.8, 6.35];
/** Heart-monitor trace along the line: [x offset from centre, y offset], scaled by the beat. */
const ECG = [
  [-1008, 0],
  [-170, 0],
  [-140, -28],
  [-110, 0],
  [-45, 0],
  [-28, 34],
  [0, -250],
  [30, 95],
  [58, 0],
  [115, 0],
  [155, -48],
  [195, 0],
  [1008, 0],
];

type Points = number[];
type Pt = [number, number];

const lerp = (a: Pt, b: Pt, t: number): Pt => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
];

const split = (p0: Pt, p1: Pt, p2: Pt, p3: Pt, t: number): [Pt[], Pt[]] => {
  const a = lerp(p0, p1, t);
  const b = lerp(p1, p2, t);
  const c = lerp(p2, p3, t);
  const d = lerp(a, b, t);
  const e = lerp(b, c, t);
  const f = lerp(d, e, t);
  return [
    [p0, a, d, f],
    [f, e, c, p3],
  ];
};

const buildHeart = (): Points => {
  // Upright heart (lobes on top, point at the bottom), traced right lobe first.
  const h4 = [
    208, 182.5, 179.8, 60.3, -31.7, 46.2, -36.4, 210.7, -41.1, 335.2, 99.9,
    422.2, 208, 530.3, 316.1, 422.2, 457.1, 335.2, 452.4, 210.7, 447.7, 46.2,
    236.2, 60.3, 208, 182.5,
  ];
  const rev: number[] = [];
  for (let i = h4.length - 2; i >= 0; i -= 2) rev.push(h4[i], h4[i + 1]);
  // Split each of the 4 curves in three so the heart has 12, like every other shape.
  const heart = [rev[0], rev[1]];
  for (let i = 2; i < rev.length; i += 6) {
    const p0: Pt = [rev[i - 2], rev[i - 1]];
    const p1: Pt = [rev[i], rev[i + 1]];
    const p2: Pt = [rev[i + 2], rev[i + 3]];
    const p3: Pt = [rev[i + 4], rev[i + 5]];
    const [s1, rest] = split(p0, p1, p2, p3, 1 / 3);
    const [s2, s3] = split(rest[0], rest[1], rest[2], rest[3], 0.5);
    [s1, s2, s3].forEach((s) =>
      heart.push(s[1][0], s[1][1], s[2][0], s[2][1], s[3][0], s[3][1]),
    );
  }
  return heart;
};

export const HEART = buildHeart();

/** The heart turned a quarter-turn to point right, like the paperclip mark. */
export const HEART_RIGHT: Points = (() => {
  const out: number[] = [];
  for (let i = 0; i < HEART.length; i += 2) {
    out.push(CX + (HEART[i + 1] - CY), CY - (HEART[i] - CX));
  }
  return out;
})();

/** Centre line of the paperclip wire. */
export const CLIP: Points = [
  92.4, 232.2, 79.2, 219.1, 66, 205.9, 52.8, 192.8, 14.1, 154.1, 14.1, 91.5,
  52.8, 52.8, 91.5, 14.1, 154.1, 14.1, 192.8, 52.8, 249.2, 109.2, 305.6, 165.6,
  362, 222, 400.7, 260.7, 400.7, 323.3, 362, 362, 305.6, 418.4, 249.2, 474.8,
  192.8, 531.2, 154.1, 569.9, 91.5, 569.9, 52.8, 531.2, 14.1, 492.5, 14.1,
  429.9, 52.8, 391.2, 109.2, 334.8, 165.6, 278.4, 222, 222, 260.7, 183.3, 323.3,
  183.3, 362, 222, 400.7, 260.7, 400.7, 323.3, 362, 362, 323.3, 400.7, 260.7,
  400.7, 222, 362,
];

/** A long straight line across the screen. */
export const LINE: Points = Array.from({ length: 37 }, (_, i) => [
  -800 + i * (LINE_LENGTH / 36),
  CY,
]).flat();

const clamp = (v: number) => Math.max(0, Math.min(1, v));
const seg = (t: number, a: number, b: number) => clamp((t - a) / (b - a));
const easeOut = (x: number) => 1 - Math.pow(1 - x, 3);
const easeInOut = (x: number) =>
  x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;

export const mix = (a: Points, b: Points, t: number): Points =>
  a.map((v, i) => v + (b[i] - v) * t);

const rotate = (p: Points, angle: number): Points => {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const out: number[] = [];
  for (let i = 0; i < p.length; i += 2) {
    const dx = p[i] - CX;
    const dy = p[i + 1] - CY;
    out.push(CX + dx * c - dy * s, CY + dx * s + dy * c);
  }
  return out;
};

export const pointsToPath = (p: Points): string => {
  const f = (n: number) => n.toFixed(1);
  let d = `M${f(p[0])} ${f(p[1])}`;
  for (let i = 2; i < p.length; i += 6) {
    d += ` C${f(p[i])} ${f(p[i + 1])} ${f(p[i + 2])} ${f(p[i + 3])} ${f(p[i + 4])} ${f(p[i + 5])}`;
  }
  return d;
};

export interface FadeFrame {
  opacity: number;
  translateY: number;
}

export interface LogoFrame {
  ecg: { d: string; visible: boolean };
  line: {
    d: string;
    /** Set only while the line first sweeps in. */
    dash: { array: string; offset: number } | null;
    scale: number;
    /** Glow on each heart pulse, 0–1. */
    pulse: number;
    opacity: number;
  };
  mark: { opacity: number; scale: number };
  /** Horizontal shift of the logo as the name writes in, in px. */
  slideX: number;
  nameClipWidth: number;
  nameShiftX: number;
  title: FadeFrame;
  captions: [FadeFrame, FadeFrame, FadeFrame];
  box: FadeFrame;
}

/**
 * The logo sequence at `t` seconds. `slideWidth` is the rendered width of
 * the logo in px (it slides left by the mark's share of it).
 */
export const logoFrame = (t: number, slideWidth: number): LogoFrame => {
  const pDraw = easeOut(seg(t, 0.2, 0.9));

  const beatAmp = (c: number) => {
    const d = t - c;
    return d < 0 ? Math.max(0, 1 + d / 0.1) : Math.max(0, 1 - d / 0.32);
  };
  const amp = Math.max(...ECG_BEATS.map(beatAmp));
  const inEcg = t >= 0.95 && t < 3.9;
  const ecgD =
    "M" +
    ECG.map(([x, y]) => `${CX + x} ${(CY + y * amp).toFixed(1)}`).join(" L");

  const pHeart = seg(t, 3.9, 5.1);
  const pTurn = easeInOut(seg(t, 6.7, 7.4));
  const pClip = seg(t, 7.4, 8.5);
  let pts: Points;
  if (t < 6.7) pts = mix(LINE, HEART, easeInOut(pHeart));
  else if (t < 7.4) pts = rotate(HEART, (-Math.PI / 2) * pTurn);
  else pts = mix(HEART_RIGHT, CLIP, easeInOut(pClip));

  // A strong beat then a softer echo ("lub-dub").
  const bump = (c: number, w: number) => {
    const d = t - c;
    return d < 0 ? Math.max(0, 1 + d / 0.07) : Math.max(0, 1 - d / w);
  };
  let pulse = 0;
  HEART_BEATS.forEach((c) => {
    pulse = Math.max(pulse, bump(c, 0.22), 0.45 * bump(c + 0.2, 0.2));
  });

  const pMark = easeOut(seg(t, 8.5, 9.1));
  const pName = easeInOut(seg(t, 9.1, 10.0));
  const pWrite = easeOut(seg(t, 9.2, 10.1));

  const fade = (a: number, b: number): FadeFrame => ({
    opacity: Math.max(
      0,
      Math.min(easeOut(seg(t, a, a + 0.45)), 1 - easeInOut(seg(t, b - 0.4, b))),
    ),
    translateY: 12 * (1 - easeOut(seg(t, a, a + 0.45))),
  });
  const pOut = easeInOut(seg(t, 11.2, LOGO_DURATION));

  return {
    ecg: { d: ecgD, visible: inEcg },
    line: {
      d: pointsToPath(pts),
      dash:
        t < 0.9
          ? {
              array: `${LINE_LENGTH} ${LINE_LENGTH}`,
              offset: LINE_LENGTH * (1 - pDraw),
            }
          : null,
      scale: 1 + 0.11 * pulse,
      pulse,
      opacity: inEcg ? 0 : 1 - pMark,
    },
    mark: { opacity: pMark, scale: 0.97 + 0.03 * pMark },
    slideX: (slideWidth || 760) * (1130 / 2676.68) * (1 - pName),
    nameClipWidth: 2230 * pWrite,
    nameShiftX: -70 * (1 - pWrite),
    title: fade(0, 99),
    captions: [fade(1.0, 3.8), fade(5.1, 6.6), fade(10.0, 99)],
    box: { opacity: 1 - pOut, translateY: -30 * pOut },
  };
};
